import React, { useState } from "react";

const WIDGET_BUNDLE_URL =
  process.env.REACT_APP_WIDGET_BUNDLE_URL ||
  "https://chandini-project.web.app/glopal-bot.js";

// The embed snippet: one tag carrying only the bot id. The widget fetches the
// bot's name, colour, avatar and messages itself, so settings saved here reach
// the website without the snippet being pasted again — and there is nothing in
// it that a quote or a line break in those settings could break.
const buildEmbedScript = ({ bot_id }) =>
  `<script src="${WIDGET_BUNDLE_URL}" data-bot-id="${bot_id}" async></script>`;

const HTML_STEPS = [
  {
    title: "Open your HTML file",
    desc: "Find the file where your website's HTML lives (often index.html or a template file).",
  },
  {
    title: "Locate the closing </body> tag",
    desc: "Scroll to the bottom of the file until you see </body>.",
  },
  {
    title: "Paste the embed script",
    desc: "Paste the <script> tag immediately before </body>. Save the file.",
  },
  {
    title: "Open your website",
    desc: "Refresh your browser. The chat button should appear in the bottom-right corner.",
  },
];

// Through a header/footer code plugin rather than the theme's footer.php: a
// theme update overwrites footer.php, block themes have none, and many hosts
// turn the theme file editor off.
const WP_STEPS = [
  {
    title: "Log in to WordPress Admin",
    desc: "Go to yoursite.com/wp-admin and sign in.",
  },
  {
    title: "Install the WPCode plugin",
    desc: "Go to Plugins → Add New, search for “WPCode”, then install and activate “WPCode – Insert Headers and Footers”.",
  },
  {
    title: "Open Header & Footer",
    desc: "In the left sidebar, click Code Snippets → Header & Footer.",
  },
  {
    title: "Paste into the Footer box",
    desc: "Paste the <script> tag into the Footer section, then click Save Changes.",
  },
  {
    title: "Open your website",
    desc: "Reload your site — the chat button will appear in the bottom-right corner.",
  },
];

const Step = ({ number, title, desc }) => (
  <div className="flex gap-4 items-start">
    <div className="w-8 h-8 rounded-full bg-orange-500 flex items-center justify-center text-white font-bold text-sm shrink-0 mt-0.5">
      {number}
    </div>
    <div>
      <p className="font-semibold text-gray-800 dark:text-gray-100 text-sm">
        {title}
      </p>
      <p className="text-gray-500 dark:text-gray-400 text-sm mt-0.5">{desc}</p>
    </div>
  </div>
);

const CodeBlock = ({ code }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(code).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <div className="relative bg-gray-900 rounded-xl overflow-hidden">
      <pre className="text-sm text-green-400 p-5 overflow-x-auto whitespace-pre">
        {code}
      </pre>
      <button
        onClick={handleCopy}
        className="absolute top-3 right-3 px-3 py-1 bg-gray-700 hover:bg-gray-600 text-white text-xs rounded-lg transition-colors"
      >
        {copied ? "Copied!" : "Copy"}
      </button>
    </div>
  );
};

const EmbedTab = ({ bot }) => {
  const [showGuide, setShowGuide] = useState(false);
  const embedCode = buildEmbedScript(bot);

  // ── Step-by-step installation guide (inline sub-view) ──────────────────
  if (showGuide) {
    return (
      <div className="space-y-8 max-w-2xl">
        <button
          onClick={() => setShowGuide(false)}
          className="inline-flex items-center gap-1.5 text-sm font-medium text-orange-500 hover:text-orange-600 transition-colors"
        >
          <svg
            className="w-4 h-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M15 19l-7-7 7-7"
            />
          </svg>
          Back to embed code
        </button>

        <section>
          <h2 className="text-lg font-semibold text-gray-800 dark:text-gray-100 mb-3">
            Your Embed Script
          </h2>
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
            Copy the script tag below and paste it into your website's HTML.
          </p>
          <CodeBlock code={embedCode} />
        </section>

        <section>
          <h2 className="text-lg font-semibold text-gray-800 dark:text-gray-100 mb-4">
            Installing on any HTML website
          </h2>
          <div className="space-y-5">
            {HTML_STEPS.map((s, i) => (
              <Step key={i} number={i + 1} title={s.title} desc={s.desc} />
            ))}
          </div>
        </section>

        <section>
          <h2 className="text-lg font-semibold text-gray-800 dark:text-gray-100 mb-4">
            Installing on WordPress
          </h2>
          <div className="space-y-5">
            {WP_STEPS.map((s, i) => (
              <Step key={i} number={i + 1} title={s.title} desc={s.desc} />
            ))}
          </div>
        </section>

        <div className="bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800/50 rounded-xl p-4 text-sm text-orange-800 dark:text-orange-300 space-y-2">
          <p>
            <strong>Not seeing the chat button?</strong>
          </p>
          <ul className="list-disc pl-5 space-y-1">
            <li>
              Check the &lt;script&gt; tag is on the page and clear your browser
              cache (Ctrl + Shift + R).
            </li>
            <li>
              If the CORS Setting tab lists any domains, your website's domain
              must be one of them.
            </li>
            <li>
              Caching plugins with a “delay JavaScript” option can hold the
              button back until the visitor first scrolls or clicks — exclude
              glopal-bot.js from it.
            </li>
            <li>
              On WordPress.com (not self-hosted WordPress), adding custom code
              needs the Business plan or higher.
            </li>
          </ul>
        </div>
      </div>
    );
  }

  // ── Default view: the embed code + entry to the guide ──────────────────
  return (
    <div className="space-y-5 max-w-2xl">
      <p className="text-sm text-gray-500 dark:text-gray-400">
        Copy the script below and paste it into your website just before the
        closing{" "}
        <code className="bg-gray-100 dark:bg-gray-700 dark:text-gray-300 px-1 rounded">
          &lt;/body&gt;
        </code>{" "}
        tag.
      </p>

      <CodeBlock code={embedCode} />

      <p className="text-xs text-gray-400 dark:text-gray-500">
        Changes you save in Settings reach your website on their own, within
        about a minute — no need to paste the code again.
      </p>

      <button
        onClick={() => setShowGuide(true)}
        className="text-sm font-medium text-orange-500 hover:text-orange-600 underline transition-colors"
      >
        View step-by-step installation guide →
      </button>
    </div>
  );
};

export default EmbedTab;
